import { useEffect, useMemo, useRef, useState, Fragment, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useJsApiLoader, GoogleMap, StandaloneSearchBox, InfoWindowF, MarkerF, MarkerClustererF, PolygonF, CircleF, PolylineF } from '@react-google-maps/api';
import mapStyles from './styles/mapStyles';
import styles from './styles/customMap.module.css';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from '../utils/constants';

const MapControl = ({ position, map, children }) => {
  const [controlContainer, setControlContainer] = useState(null);

  useEffect(() => {
    if (!map || !window.google) return;
    const container = document.createElement('div');
    map.controls[window.google.maps.ControlPosition[position]].push(container);
    setControlContainer(container);
    return () => {
      const controlArray = map.controls[window.google.maps.ControlPosition[position]];
      for (let i = 0; i < controlArray.length; i++) {
        if (controlArray.getAt(i) === container) {
          controlArray.removeAt(i);
          break;
        }
      }
    };
  }, [map, position]);

  if (!controlContainer) return null;
  return createPortal(children, controlContainer);
};

const MapZone = (props) => {
  const {
    polygons,
    /*markers,*/
    driversMarkers,
    branchesMarkers,
    defaultCenter,
    /*centerZoom,*/
    selectedDriverMarker,
    setSelectedDriverMarker,
    setPolygons,
    loadDraw
  } = props;
  const mapRef = useRef();
  const polygonRefs = useRef([]);
  const markerRefs = useRef([]);
  const activePolygonIndex = useRef();
  const autocompleteRef = useRef();

  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [polygonHistory, setPolygonHistory] = useState([]);

  const { isLoaded, /*loadError*/ } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  const [loadBranchesMarkers, setLoadBranchesMarkers] = useState();

  const defaultStyleOptions = useMemo(() => ({
    strokeOpacity: 0.5,
    strokeWeight: 2,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true
  }), []);

  const closeStyleOptions = useMemo(() => ({
    ...defaultStyleOptions,
    zIndex: 3,
    fillOpacity: 0.05,
    strokeColor: "#ff5252",
    fillColor: "#ff5252"
  }), [defaultStyleOptions])

  const middleStyleOptions = useMemo(() => ({
    ...defaultStyleOptions,
    zIndex: 2,
    fillOpacity: 0.05,
    strokeColor: "#fbc02d",
    fillColor: "#fbc02d"
  }
  ), [defaultStyleOptions])

  useEffect(() => {
    const bmark = branchesMarkers.map((iterator, index) => {
      return (
        <Fragment key={index}>
          <MarkerF
            onLoad={(event) => onLoadMarker(event, index)}
            title={iterator.title}
            position={iterator.position}
            icon={iterator.icon}
            options={iterator.markeroptions}
          />
          <CircleF center={iterator.position} radius={1500} options={closeStyleOptions} />
          <CircleF center={iterator.position} radius={3500} options={middleStyleOptions} />
        </Fragment>
      )
    });
    setLoadBranchesMarkers(bmark);
  }, [branchesMarkers, closeStyleOptions, middleStyleOptions]);

  // Smooth pan to selected driver
  useEffect(() => {
    if (selectedDriverMarker && mapRef.current) {
      if (selectedDriverMarker.position && typeof selectedDriverMarker.position.lat === 'number') {
        mapRef.current.panTo(selectedDriverMarker.position);
        mapRef.current.setZoom(14); // Zoom closer
      }
    }
  }, [selectedDriverMarker]);

  const containerStyle = {
    width: '100%',
    height: props.mapHeight || '400px',
  }

  const autocompleteStyle = {
    boxSizing: 'border-box',
    border: '1px solid transparent',
    width: '240px',
    height: '38px',
    padding: '0 12px',
    borderRadius: '3px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
    fontSize: '14px',
    outline: 'none',
    textOverflow: 'ellipses',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    top: '55px',
  }

  // The deleteIconStyle is removed since we moved it to the toolbar

  const polygonOptions = {
    fillOpacity: 0.3,
    fillColor: '#ff0000',
    strokeColor: '#ff0000',
    strokeWeight: 2,
    draggable: true,
    editable: true
  }

  const drawingPolylineOptions = {
    strokeColor: '#ff0000',
    strokeOpacity: 0.8,
    strokeWeight: 2,
  }

  const [mapInstance, setMapInstance] = useState(null);

  const onLoadMap = (map) => {
    mapRef.current = map;
    setMapInstance(map);
  }

  const onLoadPolygon = (polygon, index) => {
    polygonRefs.current[index] = polygon;
  }

  const onLoadMarker = (marker, index) => {
    markerRefs.current[index] = marker;
  }

  const onClickPolygon = (index) => {
    activePolygonIndex.current = index;
  }

  const onLoadAutocomplete = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  }

  const onPlacesChanged = () => {
    const places = autocompleteRef.current.getPlaces();
    if (places && places.length > 0) {
      const { geometry } = places[0];
      const bounds = new window.google.maps.LatLngBounds();
      if (geometry.viewport) {
        bounds.union(geometry.viewport);
      } else {
        bounds.extend(geometry.location);
      }
      mapRef.current.fitBounds(bounds);
    }
  }

  // --- Manual Drawing Logic ---

  const startDrawing = () => {
    setIsDrawing(true);
    setDrawingPoints([]);
  }

  const cancelDrawing = () => {
    setIsDrawing(false);
    setDrawingPoints([]);
  }

  const undoLastPoint = () => {
    setDrawingPoints(prev => prev.slice(0, -1));
  }

  const pushHistory = () => {
    setPolygonHistory(prev => {
      if (prev.length > 0 && prev[prev.length - 1] === polygons) {
        return prev;
      }
      return [...prev, polygons];
    });
  }

  const finishDrawing = useCallback(() => {
    if (drawingPoints.length < 3) return;

    const correla = polygons.length === 0 ? 1 : polygons.length + 1;
    const newPolygon = [...drawingPoints];
    const startPoint = newPolygon[0];
    newPolygon.push(startPoint);
    pushHistory();
    setPolygons([...polygons, { id_polygon: correla, coord: newPolygon }]);
    setIsDrawing(false);
    setDrawingPoints([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawingPoints, polygons, setPolygons]);

  const onMapClick = useCallback((e) => {
    if (!isDrawing) return;

    const clickedLat = e.latLng.lat();
    const clickedLng = e.latLng.lng();
    const newPoint = { lat: clickedLat, lng: clickedLng };

    if (drawingPoints.length >= 3) {
      const firstPoint = drawingPoints[0];
      const projection = mapRef.current.getProjection();
      if (projection) {
        const firstPixel = projection.fromLatLngToPoint(
          new window.google.maps.LatLng(firstPoint.lat, firstPoint.lng)
        );
        const clickPixel = projection.fromLatLngToPoint(
          new window.google.maps.LatLng(clickedLat, clickedLng)
        );
        const zoom = mapRef.current.getZoom();
        const scale = Math.pow(2, zoom);
        const dx = (firstPixel.x - clickPixel.x) * scale;
        const dy = (firstPixel.y - clickPixel.y) * scale;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 15) {
          finishDrawing();
          return;
        }
      }
    }

    setDrawingPoints(prev => [...prev, newPoint]);
  }, [isDrawing, drawingPoints, finishDrawing]);

  // --- End Manual Drawing Logic ---

  const onDeleteDrawing = () => {
    // Si solo hay un polígono, asume que es ese el que se quiere borrar
    if (polygons.length === 1) {
      pushHistory();
      setPolygons([]);
      activePolygonIndex.current = undefined;
      return;
    }
    
    // Si hay múltiples, requiere que uno haya sido seleccionado previamente
    if (activePolygonIndex.current !== undefined) {
      pushHistory();
      const filtered = polygons.filter((polygon, index) => index !== activePolygonIndex.current);
      setPolygons(filtered);
      activePolygonIndex.current = undefined;
    } else {
      // Opcional: mostrar un alert si hay varios y ninguno seleccionado
      alert("Haz clic en un polígono para seleccionarlo antes de borrar.");
    }
  }

  const lastEditTime = useRef(0);

  const onEditPolygon = (index) => {
    const now = Date.now();
    if (now - lastEditTime.current < 500) return; // Debounce rapid consecutive events (e.g., onMouseUp followed by onDragEnd)
    lastEditTime.current = now;

    const polygonRef = polygonRefs.current[index];
    if (polygonRef) {
      const coordinates = polygonRef.getPath()
        .getArray()
        .map(latLng => ({ lat: latLng.lat(), lng: latLng.lng() }));

      const oldCoordinates = polygons[index].coord;
      
      // Compare to avoid pushing to history on normal clicks or micro floating-point changes
      const isChanged = coordinates.length !== oldCoordinates.length || coordinates.some((c, i) => {
        return Math.abs(c.lat - oldCoordinates[i].lat) > 0.000001 || Math.abs(c.lng - oldCoordinates[i].lng) > 0.000001;
      });

      if (isChanged) {
        pushHistory();
        let allPolygons = [...polygons];
        allPolygons[index] = { id_polygon: allPolygons[index].id_polygon, coord: coordinates };
        setPolygons(allPolygons);
      }
    }
  }

  const undoPolygonChange = () => {
    if (polygonHistory.length > 0) {
      const prev = polygonHistory[polygonHistory.length - 1];
      setPolygons(prev);
      setPolygonHistory(prevHistory => prevHistory.slice(0, -1));
    }
  };

  const handleCenterChanged = () => {
    if (mapRef.current) {
      mapRef.current.getCenter();
    }
  };

  // SVG icon for the polygon draw button
  const PolygonIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 5 L12 2 L21 5 L19 19 L5 19 Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <circle cx="3" cy="5" r="1.5" />
      <circle cx="12" cy="2" r="1.5" />
      <circle cx="21" cy="5" r="1.5" />
      <circle cx="19" cy="19" r="1.5" />
      <circle cx="5" cy="19" r="1.5" />
    </svg>
  );

  const TrashIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
      <line x1="10" y1="11" x2="10" y2="17"></line>
      <line x1="14" y1="11" x2="14" y2="17"></line>
    </svg>
  );

  const UndoIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7v6h6"></path>
      <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"></path>
    </svg>
  );

  return (
    isLoaded
      ?
      <div className='map-container' style={{ position: 'relative', height: '100%' }}>
        {/* -- Drawing Toolbar -- */}
        {loadDraw && (
          <div className={styles.drawingToolbar}>
            {!isDrawing ? (
              <>
                <button
                  type="button"
                  className={styles.drawingBtn}
                  onClick={startDrawing}
                  title="Dibujar polígono"
                >
                  <PolygonIcon /> Dibujar
                </button>
                {polygons && polygons.length > 0 && (
                  <button
                    type="button"
                    className={styles.drawingBtnDelete}
                    onClick={onDeleteDrawing}
                    title="Eliminar polígono seleccionado"
                  >
                    <TrashIcon /> Borrar
                  </button>
                )}
                {polygonHistory.length > 0 && (
                  <button
                    type="button"
                    className={styles.drawingBtn}
                    onClick={undoPolygonChange}
                    title="Deshacer cambio en el polígono"
                  >
                    <UndoIcon /> Deshacer
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.drawingBtnFinish}
                  onClick={finishDrawing}
                  disabled={drawingPoints.length < 3}
                  title="Finalizar polígono"
                >
                  ✓ Finalizar
                </button>
                {drawingPoints.length > 0 && (
                  <button
                    type="button"
                    className={styles.drawingBtnCancel}
                    onClick={undoLastPoint}
                    title="Deshacer último punto"
                  >
                    <UndoIcon /> Deshacer
                  </button>
                )}
                <button
                  type="button"
                  className={styles.drawingBtnCancel}
                  onClick={cancelDrawing}
                  title="Cancelar dibujo"
                >
                  ✕ Cancelar
                </button>
              </>
            )}
          </div>
        )}
        <GoogleMap
          zoom={loadDraw ? 15 : 10}
          center={loadDraw && branchesMarkers?.length > 0 ? branchesMarkers[0].position : defaultCenter}
          onCenterChanged={handleCenterChanged}
          onLoad={onLoadMap}
          mapContainerStyle={containerStyle}
          options={{
            styles: mapStyles,
            draggableCursor: isDrawing ? 'crosshair' : undefined,
          }}
          onClick={onMapClick}
        >
          {props.children && mapInstance && (
            <MapControl position="LEFT_BOTTOM" map={mapInstance}>
              {props.children}
            </MapControl>
          )}
          {/* -- POLYLINE TEMPORAL DURANTE DIBUJO -- */}
          {isDrawing && drawingPoints.length > 0 && (
            <>
              <PolylineF
                path={drawingPoints}
                options={drawingPolylineOptions}
              />
              {/* Marcador en el primer punto para indicar cierre */}
              {drawingPoints.length >= 3 && (
                <MarkerF
                  position={drawingPoints[0]}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 7,
                    fillColor: '#ff0000',
                    fillOpacity: 0.6,
                    strokeColor: '#ffffff',
                    strokeWeight: 2,
                  }}
                  title="Click para cerrar polígono"
                />
              )}
              {/* Marcadores en cada vértice */}
              {drawingPoints.map((point, idx) => (
                <MarkerF
                  key={`draw-vertex-${idx}`}
                  position={point}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 4,
                    fillColor: '#ff0000',
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: 1,
                  }}
                />
              ))}
            </>
          )}
          {/* -- AGREGAMOS MARCADORES de Branches -- */}
          {loadBranchesMarkers}
          {/* -- AGREGAMOS MARCADORES de Drivers -- */}
          {driversMarkers && (
            <MarkerClustererF>
              {(clusterer) => {
                return driversMarkers.map((iterator, index) => {
                  return (
                    <MarkerF
                      key={index}
                      title={iterator.title}
                      position={iterator.position}
                      icon={{ url: iterator.icon.url, scaledSize: new window.google.maps.Size(40, 64) }}
                      options={iterator.markeroptions}
                      clusterer={clusterer}
                      onLoad={(event) => onLoadMarker(event, index)}
                      onClick={() => { setSelectedDriverMarker(iterator); }}
                    />
                  )
                })
              }
              }
            </MarkerClustererF>
          )
          }
          {selectedDriverMarker && (
            <InfoWindowF
              options={{ pixelOffset: new window.google.maps.Size(0, -55) }}
              position={selectedDriverMarker.position}
              onCloseClick={() => { setSelectedDriverMarker(null) }}>
              <div className='infoContainer'>
                <h3 className={styles.infoHead}>{selectedDriverMarker.title}</h3>
                <hr />
                {Array.isArray(selectedDriverMarker.orders) && selectedDriverMarker.orders.map(o => {
                  return (
                    <div key={o.id} className={styles.infoContent}>
                      <div className={styles.infoText}><span>Ticket:</span> {o.ticket}</div>
                      <div className={styles.infoText}><span>Pago:</span> {o.payment_method === 1 ? "Efectivo" : "Tarjeta"}</div>
                      <div className={styles.infoText}><span>Monto:</span> $ {o.total.toFixed(2)}</div>
                      {o.payment_method === 1 &&
                        <div className={styles.infoText}><span>Cambio para:</span> $ {o.cashback}</div>
                      }
                    </div>
                  )
                })}
              </div>
            </InfoWindowF>
          )
          }
          {/* -- AGREGAMOS POLIGONOS -- */}
          {
            polygons && (
              polygons.map((iterator, index) => (
                <PolygonF
                  key={index}
                  onLoad={(event) => onLoadPolygon(event, index)}
                  onClick={() => onClickPolygon(index)}
                  onMouseDown={() => onClickPolygon(index)}
                  onMouseUp={() => onEditPolygon(index)}
                  onDragEnd={() => onEditPolygon(index)}
                  options={polygonOptions}
                  paths={iterator.coord}
                  draggable
                  editable
                />
              ))
            )
          }
          {!loadDraw && (
            <StandaloneSearchBox
              onLoad={onLoadAutocomplete}
              onPlacesChanged={onPlacesChanged}
            >
              <input
                type='text'
                placeholder='Search Location'
                style={autocompleteStyle}
              />
            </StandaloneSearchBox>
          )}
        </GoogleMap>
      </div>
      :
      null
  );
}

export default MapZone; 